// src/components/PaymentSheet.tsx
import React, { useMemo, useState } from "react";
import type { Basket } from "./ShopModal";

type Props = {
  open: boolean;
  basket: Basket | null;
  onClose: () => void;
  onPay: (basket: Basket) => void | Promise<void>;
};

function tg() {
  return (window as any)?.Telegram?.WebApp;
}

export function PaymentSheet({ open, basket, onClose, onPay }: Props) {
  const [processing, setProcessing] = useState(false);

  const hasTime = !!basket && Number(basket.timeSec || 0) > 0;
  const hasHint = !!basket && Number(basket.hintCount || 0) > 0;

  const totalStars = useMemo(() => {
    if (!basket) return 0;
    return Number(basket.timeStars || 0) + Number(basket.hintStars || 0);
  }, [basket]);

  // If closed or no basket – render nothing
  if (!open || !basket) return null;

  const disabled = !hasTime && !hasHint;

  const handleBackdrop = () => {
    if (!processing) onClose();
  };

  const handlePayClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();     // avoid any implicit form submit
    e.stopPropagation();
    if (disabled || processing || !basket) return;

    try {
      setProcessing(true);
      // Ensure we pass the Basket, not the click event
      await Promise.resolve(onPay(basket));
    } catch (err) {
      console.error("[PaymentSheet] Pay error:", err);
      tg()?.showAlert?.("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="pay-backdrop" role="dialog" aria-modal="true" onClick={handleBackdrop}>
      <div
        className="pay-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: "translateY(0)" }}
      >
        <div className="pay-grabber" />

        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Confirm purchase</div>

          {!hasTime && !hasHint ? (
            <div style={{ opacity: 0.85 }}>Your basket is empty.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {hasTime && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 8,
                    alignItems: "center",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    padding: "8px 10px"
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700 }}>Extra Time</div>
                    <div style={{ opacity: 0.8 }}>{basket.timeSec}s</div>
                  </div>
                  <div style={{ fontWeight: 800 }}>{basket.timeStars} ⭐</div>
                </div>
              )}

              {hasHint && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 8,
                    alignItems: "center",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    padding: "8px 10px"
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700 }}>Super Hints</div>
                    <div style={{ opacity: 0.8 }}>{basket.hintCount}</div>
                  </div>
                  <div style={{ fontWeight: 800 }}>{basket.hintStars} ⭐</div>
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
              paddingTop: 8,
              borderTop: "1px solid rgba(255,255,255,0.12)"
            }}
          >
            <div style={{ opacity: 0.9 }}>Total</div>
            <div style={{ fontWeight: 900, fontSize: 18 }}>{totalStars} ⭐</div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" className="tab-button" onClick={onClose} disabled={processing}>
              Cancel
            </button>
            <button
              type="button"                 // important: not a submit button
              className="agree-button"
              disabled={disabled || processing}
              onClick={handlePayClick}
            >
              {processing ? "Processing…" : `Pay ${totalStars} ⭐`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
